import React, { createContext, useState, useContext, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { sliceKey, reducer } from 'app/pages/Header/slice';
import { headerSaga } from 'app/pages/Header/saga';
import MiaChatV2ApiService from 'app/apiService/miaChat/miaChatV2ApiService';
import { ChatRequest, ChatResponse, Message, Operation, InitRequest, InitResponse, UpdateConversationStateRequest, UpdateConversationStateResponse } from 'app/apiService/miaChat/types';

// Import mock toggle utility for console commands
import 'app/apiService/miaChat/mockToggle';


interface MiaChatV2ContextType {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  
  operations: Operation[];
  setOperations: React.Dispatch<React.SetStateAction<Operation[]>>;
  
  currentConversation: ChatResponse | null;
  setCurrentConversation: React.Dispatch<React.SetStateAction<ChatResponse | null>>;
  
  fetchOperations: () => Promise<void>;
  initChat: (signal?: AbortSignal) => Promise<InitResponse | null>;
  startNewChat: (action: string, conversationId: string, signal?: AbortSignal) => Promise<ChatResponse | null>;
  sendChatMessage: (request: ChatRequest, signal?: AbortSignal) => Promise<ChatResponse | null>;
  updateConversationState: (request: UpdateConversationStateRequest, signal?: AbortSignal) => Promise<UpdateConversationStateResponse | null>;
  getUniqueTags: () => string[];
  getOperationsByTag: (tag: string) => Operation[];
}

// ----------- Context Create -----------
const MiaChatV2Context = createContext<MiaChatV2ContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const MiaChatV2Provider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [operations, setOperations] = useState<Operation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatResponse | null>(null);
  
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  // ----------- API Calls Using MiaChatV2 Service -----------
  const fetchOperations = async () => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.getOperations({
        dealer_id: currentDealerId
      });

      if (response.body?.operations) {
        setOperations(response.body.operations);
      } else {
        setError("No operations found");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch operations");
    } finally {
      setLoading(false);
    }
  };

  const initChat = async (signal?: AbortSignal): Promise<InitResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.initChat({
        dealer_id: currentDealerId
      }, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to initialize chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to initialize chat");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = async (selected_option: string, conversationId: string, signal?: AbortSignal): Promise<ChatResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    if (!conversationId) {
      setError("Conversation ID is required");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      // Fallback to API call for other operations
      const response = await MiaChatV2ApiService.startNewChat({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        action: "SELECT_OPERATION",
        selected_option,
        new: true,
        next_action: currentConversation?.next_action || '',
        messages: currentConversation?.messages || []
      }, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to start chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to start chat");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async (request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse | null> => {
    setLoading(true);
    setError("");
    try {
      request.next_action = currentConversation?.next_action;
      // Fallback to API call for unmocked actions
      const response = await MiaChatV2ApiService.sendChatMessage(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to send message");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to send message");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateConversationState = async (request: UpdateConversationStateRequest, signal?: AbortSignal): Promise<UpdateConversationStateResponse | null> => {
    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.updateConversationState(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to update conversation state");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to update conversation state");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ----------- Utility Functions -----------
  
  // Get unique tags from all operations with "All" as default
  const getUniqueTags = (): string[] => {
    const allTags = operations.flatMap(op => op.tags);
    const uniqueTags = Array.from(new Set(allTags));
    return ['All', ...uniqueTags];
  };

  // Get operations filtered by specific tag
  const getOperationsByTag = (tag: string): Operation[] => {
    if (tag === 'All') {
      return operations;
    }
    return operations.filter(op => op.tags.includes(tag));
  };

  return (
    <MiaChatV2Context.Provider
      value={{
        loading,
        setLoading,
        error,
        setError,
        operations,
        setOperations,
        currentConversation,
        setCurrentConversation,
        fetchOperations,
        initChat,
        startNewChat,
        sendChatMessage,
        updateConversationState,
        getUniqueTags,
        getOperationsByTag,
      }}
    >
      {children}
    </MiaChatV2Context.Provider>
  );
};

// ----------- Custom Hook -----------
export const useMiaChatV2 = (): MiaChatV2ContextType => {
  const context = useContext(MiaChatV2Context);
  if (context === undefined) {
    throw new Error("useMiaChatV2 must be used within a MiaChatV2Provider");
  }
  return context;
};
